import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { userPhotoQueryOptions } from '../../api/users';
import { getInitials } from '../../utils/initials';

const SIZES = {
  xs: 'w-9 h-9 text-sm',
  sm: 'w-11 h-11 text-base',
  md: 'w-16 h-16 text-xl',
  lg: 'w-24 h-24 text-3xl',
};

interface UserAvatarProps {
  userId: string;
  displayName: string;
  size?: keyof typeof SIZES;
}

export function UserAvatar({
  userId,
  displayName,
  size = 'sm',
}: UserAvatarProps) {
  const { data: blob } = useQuery(userPhotoQueryOptions(userId));

  // React Query caches the Blob; each mount derives its own object URL and
  // revokes it on unmount / blob change so we never leak or dangle it.
  const photoUrl = useMemo(
    // One URL per blob, revoked by the effect below on change or unmount. A
    // useMemo is not a guarantee, so StrictMode's double render can leak one
    // URL in development; production creates exactly one.
    // react-doctor-disable-next-line react-doctor/no-create-object-url-in-render, react-doctor/no-create-object-url-without-revoke
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(() => {
    if (!photoUrl) {
      return;
    }
    return () => URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  const sizeClass = SIZES[size];

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={displayName}
        className={`${sizeClass} shrink-0 rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full bg-blue/10`}
    >
      <span className="leading-none font-semibold text-blue">
        {getInitials(displayName)}
      </span>
    </div>
  );
}
